// BulkPOCreationPreview.jsx
// Preview modal for bulk PO creation from selected reorder tasks

import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import PurchasingAPI from '../../../services/purchasingService';
import { toast } from 'react-toastify';

class BulkPOCreationPreview extends Component {
    state = {
        saving: false,
        error: null
    };

    /**
     * Group selected tasks by vendor + location
     */
    getGroupedPOs = () => {
        const { tasks } = this.props;
        const groups = {};

        tasks.forEach(task => {
            const vendorId = task.preferred_vendor_id || 'unassigned';
            const locationId = task.location_id || 'all';
            const key = `${vendorId}-${locationId}`;

            if (!groups[key]) {
                groups[key] = {
                    vendor_id: task.preferred_vendor_id,
                    vendor_name: task.preferred_vendor_name || 'Unassigned Vendor',
                    location_id: task.location_id,
                    location_name: task.location_name || 'All Locations',
                    items: []
                };
            }
            groups[key].items.push(task);
        });

        return Object.values(groups);
    };

    handleConfirm = async () => {
        const taskIds = this.props.tasks.map(t => t.task_id);
        this.setState({ saving: true, error: null });
        try {
            const result = await PurchasingAPI.createDraftPOs(taskIds, this.props.installerId);
            const count = result.created_count || result.pos_created || taskIds.length;
            toast.success(`Created ${count} draft purchase order(s).`);
            this.props.onComplete();
        } catch (err) {
            console.error('Error creating draft POs:', err);
            this.setState({ saving: false, error: 'Failed to create draft POs. Please try again.' });
        }
    };

    render() {
        const { show, onHide } = this.props;
        const { saving, error } = this.state;
        const groups = this.getGroupedPOs();

        return (
            <Modal show={show} onHide={onHide} centered size="lg" backdrop={saving ? 'static' : true}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa-sharp fa-regular fa-file-plus mr-2"></i>
                        Create Draft Purchase Orders
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <div className="alert alert-danger">{error}</div>}

                    <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
                        The following draft POs will be created, grouped by vendor and location:
                    </p>

                    {groups.map((group, idx) => (
                        <div key={idx} style={{
                            background: 'var(--color-bg-panel)', border: '1px solid var(--color-border)', borderRadius: '8px',
                            padding: '16px', marginBottom: '12px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <strong style={{ color: 'var(--color-text-primary)' }}>
                                    <i className="fa-sharp fa-regular fa-truck-field mr-2"></i>
                                    {group.vendor_name}
                                </strong>
                                <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>{group.location_name}</span>
                            </div>
                            <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                                {group.items.length} line{group.items.length !== 1 ? 's' : ''}:
                                {group.items.map((item, i) => (
                                    <span key={i} style={{ marginLeft: '8px' }}>
                                        {item.product_name || item.entity_name || `Product #${item.entity_id}`}
                                        {i < group.items.length - 1 ? ',' : ''}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}

                    <div style={{ marginTop: '16px', padding: '12px', background: 'var(--color-info-light)', borderRadius: '6px', fontSize: '13px', color: 'var(--color-info-dark)' }}>
                        <i className="fa-sharp fa-regular fa-circle-info mr-2"></i>
                        {groups.length} draft PO{groups.length !== 1 ? 's' : ''} will be created. You can review and add lines before submitting.
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
                    <Button
                        style={{ backgroundColor: 'var(--color-brand-teal)', borderColor: 'var(--color-brand-teal)' }}
                        onClick={this.handleConfirm}
                        disabled={saving}
                    >
                        {saving ? (
                            <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>Creating...</>
                        ) : (
                            <><i className="fa-sharp fa-regular fa-file-plus mr-2"></i>Create {groups.length} Draft PO{groups.length !== 1 ? 's' : ''}</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default BulkPOCreationPreview;
